import { useState, useEffect } from "react";
import { usePymbleApi } from "./usePymbleApi";
import _ from "lodash";
import { useQuery, useQueryClient } from "@tanstack/react-query";

// const DICTIONARY_END_POINT = `${api.myPymble}/dictionary/`;
const DICTIONARY_END_POINT = "/dictionary/";

/**
 * Hook to fetch api data from dictionary api.
 *
 * Params:
 *   name: <string>  Name of dictionary to retrieve GroupTypes | EventTypes | etc
 */
export function useDictionary(name: string) {
  const [dictionary, setDictionary] = useState<any>([]);

  const queryClient = useQueryClient();

  const { callApiS2: callApi, isAuthenticated } = usePymbleApi();

  // Load the dictionary from api
  const queryKey = ["dictionary", name];
  const { isLoading, isError, data, error } = useQuery({
    queryKey,
    queryFn: () => callApi("GET", DICTIONARY_END_POINT + name),
    enabled: isAuthenticated,
  });

  useEffect(() => {
    let dict = preProcessDictionary(data);

    // Sort by name
    dict = sortDictionary(dict);

    if (dict) {
      setDictionary(dict);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Check for available fields and sort on that if found
  const sortDictionary = (data: any[]) => {
    return data.sort(function (a: any, b: any) {
      try {
        let nameA = "";
        let nameB = "";
        if (a?.Name && b?.Name) {
          nameA = a.Name.toUpperCase(); // ignore upper and lowercase
          nameB = b.Name.toUpperCase(); // ignore upper and lowercase
        } else if (a?.Value && b?.Value) {
          nameA = a.Value.toUpperCase(); // ignore upper and lowercase
          nameB = b.Value.toUpperCase(); // ignore upper and lowercase
        } else if (a?.Title && b?.Title) {
          nameA = a.Title.toUpperCase(); // ignore upper and lowercase
          nameB = b.Title.toUpperCase(); // ignore upper and lowercase
        }

        // Do the sort
        if (nameA && nameB) {
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
        } else {
          return 0;
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });
  };

  // Process response
  const preProcessDictionary = (res: any) => {
    let dataProcessed: any = [];
    const entry = _.find(res, { Name: name });
    if (entry?.Data) {
      dataProcessed = [...entry.Data];
    }
    return dataProcessed;
  };

  const refreshDictionary = async () => {
    queryClient.invalidateQueries({ queryKey });
  };

  /* Allows for external fetch if required */
  const fetchDictionary = async () => {
    return await queryClient.fetchQuery({
      queryKey,
      queryFn: () => callApi("GET", DICTIONARY_END_POINT + name),
    });
  };

  return {
    isLoading,
    isError,
    data,
    error,
    dictionary,
    dictionaryLoaded: !isLoading,
    refreshDictionary,
    fetchDictionary,
    preProcessDictionary,
  };
}
